import React, { useState, useRef } from 'react';

export default function ZoomableImage({ 
  src, 
  alt, 
  className = "", 
  zoomScale = 2.8,
  optimizedWidth = 800
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const containerRef = useRef(null);

  // Compute optimized CDN image URL for high clarity
  const formattedSrc = src && src.startsWith('https://assets.auxodev.dk/') && !src.includes('/cdn-cgi/image/')
    ? `https://assets.auxodev.dk/cdn-cgi/image/width=${optimizedWidth},quality=85,format=auto/${src.replace('https://assets.auxodev.dk/', '')}`
    : src;

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setPosition({ x: 50, y: 50 });
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      className={`relative overflow-hidden cursor-crosshair group ${className}`}
    >
      {/* Base image (stays normal 1x scale) */}
      <img
        src={formattedSrc}
        alt={alt}
        onError={(e) => {
          if (src && e.currentTarget.src !== src) {
            e.currentTarget.onerror = null;
            e.currentTarget.src = src;
          }
        }}
        className="w-full h-full object-cover select-none pointer-events-none"
      />

      {/* Indikator-badge før hover */}
      <div className="absolute top-3 right-3 z-10 bg-white/95 text-stone-900 border border-stone-200/80 text-[10px] font-medium px-1 py-0 rounded-full shadow-md flex items-center gap-1 transition-all duration-300 group-hover:opacity-0 group-hover:scale-95 pointer-events-none">
        <i className="fa-solid fa-magnifying-glass text-[8px] text-amber-600" aria-hidden="true" />
        <span>Hold over for zoom</span>
      </div>

      {/* Magnifying Glass Lens Circle */}
      {isHovered && (
        <div 
          className="absolute pointer-events-none rounded-full border-2 border-white/90 shadow-[0_10px_30px_rgba(0,0,0,0.45),0_0_0_1px_rgba(0,0,0,0.15)] w-48 h-48 sm:w-56 sm:h-56 -translate-x-1/2 -translate-y-1/2 z-20 overflow-hidden"
          style={{
            left: `${position.x}%`,
            top: `${position.y}%`,
            backgroundImage: `url("${formattedSrc}")`,
            backgroundPosition: `${position.x}% ${position.y}%`,
            backgroundSize: `${zoomScale * 100}%`,
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}
    </div>
  );
}
